/**
 * Disk pressure per host — the console half of #450's alarm.
 *
 * TWO CONSTRAINTS FROM THE ISSUE, and they are the whole design:
 *
 * 1. **IT NEVER TAKES ITS OWN MEASUREMENT.** Every figure here is the sample the
 *    worker sweep already stored, INCLUDING the sweep's own decision (`level`,
 *    `trend`, `ttfMs`). Nothing is re-derived from the bytes. Re-deriving would
 *    need the thresholds, the trend window and the sample history — and the
 *    moment any of those moved, this panel would disagree with the email that
 *    was actually sent. Two truthful-looking gauges about one disk, needing a
 *    runbook to reconcile, is verbatim the failure #450 exists to end.
 *
 * 2. **IT IS THE ALARM'S STATE, NOT A DASHBOARD.** Free space, trend,
 *    time-to-full, last GC, top contributors. Nothing else. #65's stated bar is
 *    that future-us can `jq` the logs; this is not the place to re-litigate it.
 *
 * AND IT IS NEVER A SUBSTITUTE FOR THE ALARM REACHING A PHONE. The console is
 * served by the API that is failing during these incidents — 2026-08-24 was
 * ENOSPC on writes, 2026-08-26 was the API down 57 minutes. This is the in-app
 * half of a pair, which is why it was built third.
 */
import { useCallback, useEffect, useState } from 'react'
import { formatGibReadable } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown } from '@papan/core-ui/ui'
import { budgetApi, type DiskHostSample, type DiskPressureResponse } from '../../client/budget'

// Types live with the client, beside the request that produces them — a second
// copy here would be the one that drifts.
// `gib` renders one-decimal GiB with a readable ` GiB` suffix. It DERIVES from
// the alarm's own `formatGib` (via `formatGibReadable` in @papan/types), so the
// figure here can never diverge from the one the email quotes — only the unit
// label differs (` GiB` vs the email's terse `G`). It was a local re-divide
// that drifted; the shared formatter is the fix.
const gib = formatGibReadable

/** Localised, because "~3h ago" is copy like any other and the sibling panel
 *  translates its row content too. `t` is threaded rather than imported so this
 *  stays a pure function of its inputs. */
type T = (key: string, opts?: Record<string, unknown>) => string
function relativeHours(iso: string, t: T): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3600_000
  if (hours < 1) return t('settings.platform.disk.agoUnderHour')
  if (hours < 48) return t('settings.platform.disk.agoHours', { n: Math.round(hours) })
  return t('settings.platform.disk.agoDays', { n: Math.round(hours / 24) })
}

/**
 * STALENESS IS A FINDING. A host that stopped reporting is exactly the case the
 * self-report probe exists for, and a panel that renders its last known 40% as
 * though it were current would be the same class of lie as `/health` reporting
 * the wrong disk.
 */
const STALE_AFTER_MS = 30 * 60 * 1000

export function DiskPressurePanel() {
  const { t } = useTranslation('core-ui')
  const [data, setData] = useState<DiskPressureResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getDiskPressure())
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load disk pressure')
    } finally {
      setLoading(false)
    }
  }, [])

  // THREE states, deliberately not two:
  //   undefined — an API predating the field (rolling restart); `.length` on it
  //               would blank the panel exactly when someone is looking at a disk
  //   null      — the query failed, and saying "no tenants" there would be a lie
  //   []        — it succeeded and found nothing, which on a filling volume is
  //               itself worth saying
  // `?? []` for the arrays: an API predating a field omits it, and `.length` on
  // `undefined` would blank the panel exactly when someone is looking at a disk.
  const hosts = data?.hosts ?? []
  const topTenants = data?.topTenants ?? []
  /**
   * Did this leg FAIL, as opposed to being genuinely empty?
   *
   * Every leg's empty/null value already means something this panel states out
   * loud — "no host is reporting", "the GC has never run, that is likely the
   * cause" — and each is FALSE if the query merely failed. So failure is asked
   * as its own question rather than read off a value that means something else.
   *
   * Defaults to "not failed" on an API predating the field: an older instance
   * genuinely had no failures to report, and assuming failure would replace
   * real data with a shrug.
   */
  const failed = (leg: string) => (data?.unavailable ?? []).includes(leg)

  useEffect(() => { void load() }, [load])
  // The shelf deck stays MOUNTED behind `display: none`, so the mount effect
  // above would otherwise be the only fetch this panel ever makes — serving
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. On a disk panel that is the worst possible staleness.
  useRefreshWhenShown(() => { void load() })

  return (
    <div className="mt-8 mb-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="themed-text text-lg font-medium">
          {t('settings.platform.disk.title')}
        </h2>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="text-xs themed-text-muted hover:themed-text transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          {t('settings.platform.disk.refresh')}
        </button>
      </div>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.disk.desc')}
      </p>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {data && failed('samples') && (
        // The samples query failed. Said as such, rather than as "no host is
        // reporting" — which would point at the worker when the database is
        // what broke.
        <div className="themed-text-muted text-sm">
          {t('settings.platform.disk.hostsUnavailable')}
        </div>
      )}

      {data && !failed('samples') && hosts.length === 0 && (
        // EMPTY IS A FINDING, not a blank panel. Review envs pin
        // PAPAN_SWEEP_DISK=0 (they do not own the volume they run on), and a
        // host whose worker has never booted has no rows at all.
        <div className="themed-text-muted text-sm">
          {t('settings.platform.disk.none')}
        </div>
      )}

      {data && hosts.length > 0 && (
        <div className="space-y-3">
          {hosts.map((h) => {
            const usedPct = Math.round(((h.totalBytes - h.freeBytes) / h.totalBytes) * 100)
            const stale = Date.now() - new Date(h.observedAt).getTime() > STALE_AFTER_MS
            return (
              <div key={h.host} className="themed-bg-secondary rounded-lg p-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="themed-text font-medium">{h.host}</span>
                    <span className="themed-text-muted text-xs truncate">{h.path}</span>
                  </div>
                  <LevelChip level={h.level} t={t} />
                </div>

                <div className="mt-2 flex items-baseline gap-4 flex-wrap text-sm">
                  <span className="themed-text">
                    {t('settings.platform.disk.freeOf', { free: gib(h.freeBytes), total: gib(h.totalBytes) })}
                  </span>
                  <span className="themed-text-muted">
                    {t('settings.platform.disk.used', { pct: usedPct })}
                  </span>
                  <TrendText trend={h.trend} ttfMs={h.ttfMs} t={t} />
                </div>

                <div className="mt-2 text-xs themed-text-muted">
                  {/* The observation time is part of the reading, not decoration:
                      a figure from an hour ago on a filling disk is not the
                      current state, and saying so is the panel's job. */}
                  {t('settings.platform.disk.measured', { when: relativeHours(h.observedAt, t) })}
                  {stale && (
                    <span className="text-amber-400">
                      {' '}· {t('settings.platform.disk.stale')}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {data && topTenants.length > 0 && (
        // WHO GREW — the fifth of the five things #450's Routing section says
        // this panel carries, and the one that turns "93% used" into somewhere
        // to look. Deliberately NOT per host: a tenant's bytes live wherever
        // its drive does, and splitting them by host would invent a number.
        <div className="mt-4">
          <div className="themed-text text-sm font-medium mb-1">
            {t('settings.platform.disk.topTenants')}
          </div>
          <div className="themed-text-muted text-xs mb-2">
            {t('settings.platform.disk.topTenantsHint')}
          </div>
          <div className="space-y-1">
            {topTenants.map((tenant) => (
              <div key={tenant.tenantId} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="themed-text-muted truncate">{tenant.tenantId}</span>
                <span className="themed-text tabular-nums shrink-0">{gib(tenant.bytes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && hosts.length > 0 && !failed('topTenants') && topTenants.length === 0 && (
        // `[]` on a volume that is filling is itself strange enough to say —
        // the same distinction the alarm's own payload draws between "the query
        // failed" and "the query found nothing".
        <div className="mt-4 themed-text-muted text-xs">
          {t('settings.platform.disk.topTenantsNone')}
        </div>
      )}

      {data && hosts.length > 0 && failed('topTenants') && (
        // The query failed. Stated as unavailable rather than as "none", which
        // would send an operator looking somewhere else at 03:00.
        <div className="mt-4 themed-text-muted text-xs">
          {t('settings.platform.disk.topTenantsUnavailable')}
        </div>
      )}

      {data && (
        // NO OUTER GATE ON THE FACTS THEMSELVES, and that is the fix for a
        // defect this shape caused. It read
        //
        //   data.markerCensus || data.lastBlobGc || failed(…) || failed(…)
        //
        // which made the GC line render only when something ELSE had a value.
        // With the GC never run AND no marker census every disjunct is false,
        // so the panel said NOTHING about the GC — in exactly the state the
        // line exists to report, and the one 2026-08-24 was actually in.
        //
        // The two facts are asymmetric and each now decides for itself:
        //   GC       — always has something to say (ran / never / unavailable),
        //              and "never" is the strongest line this panel carries.
        //   markers  — an absent census reports nothing; only a FAILED one does.
        <div className="mt-4 text-xs themed-text-muted space-y-1">
          {/* The same two facts the alarm email carries, from the same rows —
              so the panel and the page cannot describe one GC differently. */}
          {failed('lastBlobGc') ? (
            // NOT "has never reported". That line names a likely CAUSE and
            // sends an operator to the GC; on a failed query it would send
            // them to the wrong system entirely.
            <div>{t('settings.platform.disk.gcUnavailable')}</div>
          ) : data.lastBlobGc ? (
            <div>
              {t('settings.platform.disk.gcLastRan', {
                when: relativeHours(data.lastBlobGc.observedAt, t),
                deleted: data.lastBlobGc.deleted,
              })}
              {data.lastBlobGc.reclaimedBytes != null
                && t('settings.platform.disk.gcReclaimed', { bytes: gib(data.lastBlobGc.reclaimedBytes) })}
            </div>
          ) : (
            // Never softened into "no data": on 2026-08-24 this was the answer.
            <div className="text-amber-400">{t('settings.platform.disk.gcNever')}</div>
          )}
          {failed('markerCensus') ? (
            <div>{t('settings.platform.disk.markersUnavailable')}</div>
          ) : data.markerCensus ? (
            <div>
              {t('settings.platform.disk.markers', {
                bytes: gib(data.markerCensus.totalBytes),
                when: relativeHours(data.markerCensus.observedAt, t),
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

/** The sweep's DECISION, rendered. Never recomputed from the bytes — see the
 *  file header. */
function LevelChip({ level, t }: { level: DiskHostSample['level']; t: T }) {
  if (level === 'page') {
    return <span className="text-xs px-2 py-0.5 rounded bg-red-950 text-red-300 border border-red-800">{t('settings.platform.disk.levelPage')}</span>
  }
  if (level === 'warn') {
    return <span className="text-xs px-2 py-0.5 rounded bg-amber-950 text-amber-300 border border-amber-800">{t('settings.platform.disk.levelWarn')}</span>
  }
  return <span className="text-xs px-2 py-0.5 rounded themed-bg-tertiary themed-text-muted">{t('settings.platform.disk.levelOk')}</span>
}

/**
 * `unknown` is rendered as "not enough samples yet", NEVER as steady.
 *
 * That distinction is the whole reason `DiskTrend` is a three-state value rather
 * than a nullable number: "no data yet" and "healthy" must not look the same,
 * and a freshly restarted worker is in the first state for ~30 minutes — during
 * an incident, which is when restarts happen.
 */
function TrendText({ trend, ttfMs, t }: { trend: DiskHostSample['trend']; ttfMs: number | null; t: T }) {
  if (trend === 'unknown') {
    return <span className="themed-text-muted">{t('settings.platform.disk.noProjection')}</span>
  }
  if (trend === 'steady') {
    return <span className="themed-text-muted">{t('settings.platform.disk.steady')}</span>
  }
  const hours = ttfMs == null ? null : Math.round((ttfMs / 3600_000) * 10) / 10
  return (
    <span className="text-amber-400">
      {hours == null
        ? t('settings.platform.disk.shrinkingNoEta')
        : t('settings.platform.disk.shrinking', { hours })}
    </span>
  )
}
